import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  KeyRound, ShieldCheck, Smartphone, Monitor, Laptop, LogOut, Lock, Clock, Fingerprint,
} from "lucide-react";
import { toast } from "sonner";

function deviceIcon(device) {
  const d = (device || "").toLowerCase();
  if (d.includes("iphone") || d.includes("android")) return Smartphone;
  if (d.includes("mac") || d.includes("windows") || d.includes("linux")) return Laptop;
  return Monitor;
}

export default function SecuritySection() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [openPwd, setOpenPwd] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [next2, setNext2] = useState("");
  const [savingPwd, setSavingPwd] = useState(false);

  const [sessions, setSessions] = useState([]);
  const [mfa, setMfa] = useState({ enabled: false, ready: false, info: "" });

  const isLocal = (user?.auth_provider || "local") === "local";

  const loadSessions = () => {
    api.get("/auth/sessions").then((r) => setSessions(r.data)).catch(() => {});
  };
  const loadMfa = () => {
    api.get("/auth/mfa/status").then((r) => setMfa(r.data)).catch(() => {});
  };

  useEffect(() => {
    loadSessions();
    loadMfa();
  }, []);

  const submitPwd = async () => {
    if (next.length < 8) {
      toast.error(t.reset.passwordTooShort);
      return;
    }
    if (next !== next2) {
      toast.error(t.reset.passwordMismatch);
      return;
    }
    setSavingPwd(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      setOpenPwd(false);
      setCurrent(""); setNext(""); setNext2("");
      toast.success(t.security.security.pwdSaved);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, t.security.security.pwdError));
    } finally {
      setSavingPwd(false);
    }
  };

  const revoke = async (id) => {
    try {
      await api.delete(`/auth/sessions/${id}`);
      toast.success(t.security.security.sessionRevoked);
      loadSessions();
    } catch {
      toast.error(t.security.security.sessionRevokeError);
    }
  };

  return (
    <section className="ag-card p-7 space-y-7" data-testid="settings-security-section">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-slate-500" />
        <h2 className="font-serif text-2xl text-ag-ink">{t.security.security.title}</h2>
      </div>

      {/* Passwort ändern */}
      <div className="grid sm:grid-cols-[1fr_auto] gap-4 items-start pb-6 border-b border-slate-100">
        <div>
          <div className="font-serif text-lg text-ag-ink inline-flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-slate-400" /> {t.security.security.password}
          </div>
          <p className="text-sm text-slate-600 mt-1">
            {isLocal ? t.security.security.passwordSub : t.security.profile.googleManaged}
          </p>
        </div>
        {isLocal && (
          <Button
            onClick={() => setOpenPwd(true)} data-testid="settings-open-pwd"
            variant="outline" className="h-10 rounded-xl border-slate-200"
          >{t.security.security.changePassword}</Button>
        )}
      </div>

      {/* MFA Vorbereitung */}
      <div className="grid sm:grid-cols-[1fr_auto] gap-4 items-start pb-6 border-b border-slate-100">
        <div>
          <div className="font-serif text-lg text-ag-ink inline-flex items-center gap-2">
            <Fingerprint className="h-4 w-4 text-slate-400" /> {t.security.security.mfa}
            <Badge variant="outline" className="text-[10px] border-amber-200 bg-amber-50 text-amber-700 ml-1">
              {t.security.security.comingSoon}
            </Badge>
          </div>
          <p className="text-sm text-slate-600 mt-1 max-w-md">
            {mfa.info || t.security.security.mfaSub}
          </p>
          <div className="text-[11px] text-slate-500 mt-2">
            {t.security.security.mfaMethods}: TOTP · Passkey
          </div>
        </div>
        <Button
          disabled
          data-testid="settings-mfa-soon"
          variant="outline" className="h-10 rounded-xl border-slate-200 opacity-60 cursor-not-allowed"
        >{t.security.security.notify}</Button>
      </div>

      {/* Aktive Sitzungen */}
      <div>
        <div className="flex items-end justify-between mb-3">
          <div>
            <div className="font-serif text-lg text-ag-ink inline-flex items-center gap-2">
              <Monitor className="h-4 w-4 text-slate-400" /> {t.security.security.sessions}
            </div>
            <p className="text-sm text-slate-600 mt-1">{t.security.security.sessionsSub}</p>
          </div>
        </div>
        {sessions.length === 0 ? (
          <div className="text-sm text-slate-500 py-3" data-testid="settings-sessions-empty">{t.security.security.sessionsEmpty}</div>
        ) : (
          <ul className="space-y-2" data-testid="settings-sessions-list">
            {sessions.map((s) => {
              const Icon = deviceIcon(s.device);
              return (
                <li
                  key={s.id}
                  data-testid={`settings-session-${s.id}`}
                  className={`flex items-center gap-4 p-3 rounded-xl border ${s.is_current ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200 bg-white"}`}
                >
                  <div className="h-10 w-10 rounded-lg bg-slate-50 flex items-center justify-center shrink-0">
                    <Icon className="h-4 w-4 text-slate-600" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-medium text-ag-ink">{s.device}</span>
                      <span className="text-slate-400">·</span>
                      <span className="text-slate-600">{s.browser}</span>
                      {s.is_current && (
                        <Badge className="text-[10px] bg-emerald-100 text-emerald-700 hover:bg-emerald-100 ml-1">{t.security.security.thisDevice}</Badge>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500 flex items-center gap-3 mt-0.5">
                      <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {new Date(s.last_seen).toLocaleString()}</span>
                      <span>IP {s.ip_anon}</span>
                      <span className="capitalize">{s.method}</span>
                    </div>
                  </div>
                  {!s.is_current && (
                    <Button
                      size="sm" variant="ghost" onClick={() => revoke(s.id)}
                      data-testid={`settings-session-revoke-${s.id}`}
                      className="text-slate-500 hover:text-red-700"
                    ><LogOut className="h-3.5 w-3.5 mr-1.5" /> {t.security.security.revoke}</Button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        <div className="text-[11px] text-slate-500 mt-3 inline-flex items-center gap-1.5">
          <Lock className="h-3 w-3" /> {t.security.security.sessionsNote}
        </div>
      </div>

      <Dialog open={openPwd} onOpenChange={setOpenPwd}>
        <DialogContent data-testid="settings-pwd-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{t.security.security.changePassword}</DialogTitle>
            <DialogDescription>{t.security.security.changePasswordSub}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">{t.security.security.currentPwd}</Label>
              <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="settings-pwd-current" className="mt-1.5 h-11 bg-white" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">{t.reset.newPassword}</Label>
              <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} data-testid="settings-pwd-next" className="mt-1.5 h-11 bg-white" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">{t.reset.repeatPassword}</Label>
              <Input type="password" value={next2} onChange={(e) => setNext2(e.target.value)} data-testid="settings-pwd-next2" className="mt-1.5 h-11 bg-white" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenPwd(false)}>{t.common.cancel}</Button>
            <Button onClick={submitPwd} disabled={savingPwd || !current || !next} data-testid="settings-pwd-submit" className="bg-ag-ink hover:bg-slate-800">
              {t.common.save}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
